import {
  resolveRegisteredElementByName,
  type RegisteredElementRef,
} from '@/common/utils/resolveRegisteredElementByName'

const ELEMENT_WIRE_TYPE = /Element|RegistryItem/i

function unwrapElementWireValue(arg: unknown): unknown {
  if (!arg || typeof arg !== 'object' || Array.isArray(arg)) return arg
  const rec = arg as { type?: unknown; value?: unknown }
  if (rec.value == null || rec.type == null) return arg
  if (!ELEMENT_WIRE_TYPE.test(String(rec.type))) return arg
  return rec.value
}

function readHintString(value: unknown): string {
  if (value == null) return ''
  const text = String(value).trim()
  return text
}

/** Name / xml id from a serialized ContextItem dump (or already-resolved widget). */
export function readClientImportElementHint(arg: unknown): { name: string; id: string } {
  const raw = unwrapElementWireValue(arg)
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { name: '', id: '' }
  }
  const rec = raw as Record<string, unknown>
  const name = readHintString(rec.fieldName ?? rec.name)
  const idRaw = rec.elementId ?? rec.id
  const id = idRaw == null || idRaw === '' ? '' : String(idRaw).trim()
  return { name, id }
}

/**
 * Middle `$("fn")(…, en.getElement(name))` sends a ContextItem dump, not a widget.
 * Bind to the registered element so `setError` / getData hit the UI.
 */
export function resolveClientImportElementArg<T extends RegisteredElementRef>(
  arg: unknown,
  elMap: Record<string, T>,
  formId?: string | number,
  extraMaps?: Array<Record<string, T> | null | undefined>,
): unknown {
  if (arg == null || typeof arg !== 'object' || Array.isArray(arg)) return arg
  if (typeof (arg as { setError?: unknown }).setError === 'function') return arg

  const hint = readClientImportElementHint(arg)
  if (hint.name) {
    const byName = resolveRegisteredElementByName(elMap, hint.name, formId, extraMaps)
    if (byName) return byName
  }
  if (hint.id) {
    const byId = resolveRegisteredElementByName(elMap, hint.id, formId, extraMaps)
    if (byId) return byId
  }
  return arg
}

export function bindClientImportElementArgs<T extends RegisteredElementRef>(
  args: unknown[],
  elMap: Record<string, T>,
  formId?: string | number,
  extraMaps?: Array<Record<string, T> | null | undefined>,
): unknown[] {
  return args.map((arg) => resolveClientImportElementArg(arg, elMap, formId, extraMaps))
}
